import { useMemo, useState } from 'react'
import { orderSections, pickDefaultSection } from '@/lib/financials-sections'

type RowWithSection = { globalSectionName?: string | null }

/**
 * Derives the section sub-tab state for the financials tables.
 *
 * - `availableSections` is the ordered list of distinct section names in `rows`.
 *   Callers may supply `sectionNames` to keep empty destinations reachable.
 * - `activeSection` self-heals: if the user's selection is no longer available
 *   (e.g. after data changes), it falls back to the default section.
 * - `sectionFilteredRows` are the rows for `activeSection`, or all rows when
 *   no sections are available.
 */
export function useFinancialsSectionTabs<T extends RowWithSection>(
  rows: T[],
  sectionNames?: readonly string[]
) {
  const availableSections = useMemo(() => {
    if (sectionNames) return orderSections(sectionNames)

    const names = rows
      .map((r) => r.globalSectionName)
      .filter((n): n is string => typeof n === 'string' && n.length > 0)
    return orderSections(names)
  }, [rows, sectionNames])

  const [userSelectedSection, setUserSelectedSection] = useState<string | null>(
    null
  )

  const activeSection = useMemo(() => {
    if (
      userSelectedSection &&
      availableSections.includes(userSelectedSection)
    ) {
      return userSelectedSection
    }
    return pickDefaultSection(availableSections)
  }, [userSelectedSection, availableSections])

  const sectionFilteredRows = useMemo(() => {
    if (!activeSection) return rows
    return rows.filter((row) => row.globalSectionName === activeSection)
  }, [rows, activeSection])

  return {
    availableSections,
    activeSection,
    setUserSelectedSection,
    sectionFilteredRows,
  }
}
